// src/components/admin/AdminCondoFillPage.tsx
import React, { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import BackgroundShapes from '../common/BackgroundShapes';
import { condoService } from '../../services/condoService';
import { CreateCondoData, UpdateCondoData } from '../../types/condo';
import { boroughs, neighborhoods } from '../../data/locationData';

// Validation constants
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const MAX_CONDO_IMAGES = 5;
const ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];

const validateFile = (file: File): string | null => {
  if (!ALLOWED_TYPES.includes(file.type)) {
    return 'Invalid file type. Only JPG, PNG, and WebP are allowed.';
  }
  if (file.size > MAX_FILE_SIZE) {
    return 'File size exceeds 5MB limit.';
  }
  return null;
};

interface AdminCondoFillPageProps {
  mode?: 'admin' | 'user';
}

interface FormData {
  name: string;
  location: {
    borough: string;
    neighborhood: string;
    address: string;
  };
  price: number | '';
  bedrooms: number | '';
  bathrooms: number | '';
  sqft: number | '';
  details: {
    amenities: string[];
    description: string;
  };
  isActive: boolean;
  listingUrl: string;
  listingSource: string;
}

const AdminCondoFillPage: React.FC<AdminCondoFillPageProps> = ({ mode = 'admin' }) => {
  const isAdminMode = mode !== 'user';
  const { condoId } = useParams<{ condoId?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isEditMode = !!condoId;

  // Form state
  const [formData, setFormData] = useState<FormData>({
    name: '',
    location: {
      borough: '',
      neighborhood: '',
      address: ''
    },
    price: '',
    bedrooms: '',
    bathrooms: '',
    sqft: '',
    details: {
      amenities: [],
      description: ''
    },
    isActive: true,
    listingUrl: '',
    listingSource: 'streeteasy'
  });

  const [isDraft, setIsDraft] = useState(false);
  const [imageFiles, setImageFiles] = useState<File[]>([]);
  const [previewUrls, setPreviewUrls] = useState<string[]>([]);
  const [existingImageUrls, setExistingImageUrls] = useState<string[]>([]);
  const [amenityInput, setAmenityInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  // Load condo data if in edit mode
  useEffect(() => {
    if (isEditMode && condoId) {
      loadCondo(condoId);
    }
  }, [isEditMode, condoId]);

  // Show success message passed via navigation state (e.g. after Save redirect)
  useEffect(() => {
    const state = location.state as { successMessage?: string } | null;
    if (state?.successMessage) {
      setSuccess(state.successMessage);
      // Clear state so message doesn't re-appear on refresh
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate]);

  const loadCondo = async (id: string) => {
    try {
      setLoading(true);
      setError(null);
      const response = await condoService.getCondoById(id);

      if (response.success && response.data?.condo) {
        const condo = response.data.condo;
        const knownSources = ['streeteasy', 'zillow', 'apartments.com'];
        setFormData({
          name: condo.name,
          location: {
            borough: condo.location.borough,
            neighborhood: condo.location.neighborhood || '',
            address: condo.location.address || ''
          },
          price: condo.price ?? '',
          bedrooms: condo.bedrooms ?? '',
          bathrooms: condo.bathrooms ?? '',
          sqft: condo.sqft ?? '',
          details: {
            amenities: condo.details?.amenities || [],
            description: condo.details?.description || '',
          },
          isActive: condo.isActive !== undefined ? condo.isActive : true,
          listingUrl: condo.sourceUrl ?? '',
          listingSource: condo.source && knownSources.includes(condo.source) ? condo.source : 'streeteasy'
        });
        setIsDraft(condo.isDraft ?? false);
        if (condo.images && Array.isArray(condo.images) && condo.images.length > 0) {
          setExistingImageUrls(condo.images);
        } else {
          setExistingImageUrls([]);
        }
      } else {
        setError('Failed to load condo data');
      }
    } catch (err: any) {
      console.error('Load condo error:', err);
      setError(err.message || 'Failed to load condo data');
    } finally {
      setLoading(false);
    }
  };

  const updateField = (field: string, value: any) => {
    setFormData(prev => {
      const keys = field.split('.');
      if (keys.length === 1) {
        return { ...prev, [field]: value };
      } else if (keys.length === 2) {
        return {
          ...prev,
          [keys[0]]: {
            ...(prev[keys[0] as keyof FormData] as any),
            [keys[1]]: value
          }
        };
      } else if (keys.length === 3) {
        return {
          ...prev,
          [keys[0]]: {
            ...(prev[keys[0] as keyof FormData] as any),
            [keys[1]]: {
              ...((prev[keys[0] as keyof FormData] as any)?.[keys[1]] || {}),
              [keys[2]]: value
            }
          }
        };
      }
      return prev;
    });
    // Clear validation error for this field
    if (validationErrors[field]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleFileSelect = (files: FileList | null) => {
    if (!files || files.length === 0) return;

    // Check total count limit
    const currentTotal = imageFiles.length + existingImageUrls.length;
    if (currentTotal + files.length > 10) {
      setError(`Maximum 10 images allowed. You can add ${10 - currentTotal} more.`);
      return;
    }

    setError(null);
    const validFiles: File[] = [];
    const previewPromises: Promise<string>[] = [];

    // Validate all selected files first
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const validationErrorMsg = validateFile(file);
      if (validationErrorMsg) {
        setError(`${file.name}: ${validationErrorMsg}`);
        return;
      }
      validFiles.push(file);

      // Create preview URL promise
      const previewPromise = new Promise<string>((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          resolve(reader.result as string);
        };
        reader.readAsDataURL(file);
      });
      previewPromises.push(previewPromise);
    }

    // Wait for all previews to load, then update state
    Promise.all(previewPromises).then((previews) => {
      setPreviewUrls(prev => [...prev, ...previews]);
    });

    setImageFiles(prev => [...prev, ...validFiles]);
    // Don't clear existing images - allow adding to them
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFileSelect(e.target.files);
  };

  const removeImage = (index: number) => {
    setImageFiles(prev => prev.filter((_, i) => i !== index));
    setPreviewUrls(prev => prev.filter((_, i) => i !== index));
  };

  const removeExistingImage = (index: number) => {
    setExistingImageUrls(prev => prev.filter((_, i) => i !== index));
  };

  /** Build payload for API calls */
  const buildSubmitPayload = (): CreateCondoData => {
    const payload: CreateCondoData = {
      name: formData.name,
      location: {
        borough: formData.location.borough,
        neighborhood: formData.location.neighborhood || undefined,
        address: formData.location.address || undefined,
      },
      price: formData.price !== '' ? Number(formData.price) : undefined,
      bedrooms: formData.bedrooms !== '' ? Number(formData.bedrooms) : undefined,
      bathrooms: formData.bathrooms !== '' ? Number(formData.bathrooms) : undefined,
      sqft: formData.sqft !== '' ? Number(formData.sqft) : undefined,
      details: formData.details,
      isActive: formData.isActive,
    };
    const trimmedUrl = formData.listingUrl.trim();
    if (trimmedUrl) {
      payload.sourceUrl = trimmedUrl;
      payload.source = formData.listingSource;
    }
    return payload;
  };

  const handleSaveDraft = async () => {
    setError(null);
    setSuccess(null);

    if (!formData.name.trim()) {
      setError('Condo name is required to save a draft');
      return;
    }

    try {
      setSavingDraft(true);

      const draftPayload: CreateCondoData | UpdateCondoData = {
        ...buildSubmitPayload(),
        isDraft: true
      };

      if (!isAdminMode && isEditMode && condoId) {
        // User edit mode: update existing submission as draft
        // Omit `name` — name is locked at creation; sending it would trigger the duplicate-name check
        const userDraftPayload = { ...draftPayload, isDraft: true } as UpdateCondoData;
        delete (userDraftPayload as Record<string, unknown>).name;
        const res = await condoService.updateUserSubmission(
          condoId,
          userDraftPayload,
          imageFiles.length > 0 ? imageFiles : undefined
        );
        if (res.success) {
          navigate('/my-condos');
        } else {
          setError((res as any).message || 'Failed to save draft.');
        }
        return;
      } else if (!isAdminMode) {
        // User mode: submit as draft to /api/condos/submit, then navigate to /my-condos
        const response = await condoService.submitCondo(
          draftPayload as CreateCondoData,
          imageFiles.length > 0 ? imageFiles : undefined
        );
        if (response.success) {
          navigate('/my-condos');
        } else {
          setError(response.message || 'Failed to save draft');
        }
      } else if (isEditMode && condoId) {
        const response = await condoService.updateCondo(
          condoId,
          draftPayload as UpdateCondoData,
          imageFiles.length > 0 ? imageFiles : undefined
        );
        if (response.success) {
          setSuccess('Draft saved successfully!');
        } else {
          setError(response.message || 'Failed to save draft');
        }
      } else {
        const response = await condoService.createCondo(
          draftPayload as CreateCondoData,
          imageFiles.length > 0 ? imageFiles : undefined
        );
        if (response.success && response.data?.condo) {
          const newId = response.data.condo.id ?? response.data.condo._id;
          navigate(`/admin/condos/edit/${newId}`, { state: { successMessage: 'Draft saved successfully!' } });
        } else {
          setError(response.message || 'Failed to save draft');
        }
      }
    } catch (err: any) {
      console.error('Save draft error:', err);
      setError(err.message || 'An error occurred. Please try again.');
    } finally {
      setSavingDraft(false);
    }
  };

  const addAmenity = () => {
    if (amenityInput.trim()) {
      setFormData(prev => ({
        ...prev,
        details: {
          ...prev.details,
          amenities: [...(prev.details?.amenities || []), amenityInput.trim()]
        }
      }));
      setAmenityInput('');
    }
  };

  const removeAmenity = (index: number) => {
    setFormData(prev => ({
      ...prev,
      details: {
        ...prev.details,
        amenities: (prev.details?.amenities || []).filter((_, i) => i !== index)
      }
    }));
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.name.trim()) {
      errors.name = 'Condo name is required';
    }

    if (!formData.location.borough.trim()) {
      errors['location.borough'] = 'Borough is required';
    }

    if (!formData.location.neighborhood?.trim()) {
      errors['location.neighborhood'] = 'Neighborhood is required';
    }

    if (formData.price === '' || formData.price === undefined || formData.price === null) {
      errors.price = 'Price is required';
    } else if (Number(formData.price) < 0) {
      errors.price = 'Price must be >= 0';
    }

    if (formData.bedrooms === '' || formData.bedrooms === undefined || formData.bedrooms === null) {
      errors.bedrooms = 'Bedrooms is required';
    } else if (Number(formData.bedrooms) < 0) {
      errors.bedrooms = 'Bedrooms must be >= 0';
    }

    if (formData.bathrooms === '' || formData.bathrooms === undefined || formData.bathrooms === null) {
      errors.bathrooms = 'Bathrooms is required';
    } else if (Number(formData.bathrooms) < 0) {
      errors.bathrooms = 'Bathrooms must be >= 0';
    }

    if (formData.sqft !== '' && Number(formData.sqft) < 0) {
      errors.sqft = 'Sqft must be >= 0';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!validateForm()) {
      setError('Please fix validation errors');
      return;
    }

    try {
      setSubmitting(true);

      if (!isAdminMode && isEditMode && condoId) {
        // User edit mode: update existing submission and submit for review
        // Omit `name` — name is locked at creation; sending it would trigger the duplicate-name check
        const userSubmitPayload = { ...buildSubmitPayload(), isDraft: false } as UpdateCondoData;
        delete (userSubmitPayload as Record<string, unknown>).name;
        const res = await condoService.updateUserSubmission(
          condoId,
          userSubmitPayload,
          imageFiles.length > 0 ? imageFiles : undefined
        );
        if (res.success) {
          navigate('/my-condos');
        } else {
          setError((res as any).message || 'Failed to submit condo.');
        }
        return;
      } else if (!isAdminMode) {
        // User mode: submit to /api/condos/submit, navigate to /my-condos on success
        const response = await condoService.submitCondo(
          buildSubmitPayload(),
          imageFiles.length > 0 ? imageFiles : undefined
        );
        if (response.success) {
          navigate('/my-condos');
        } else {
          setError(response.message || 'Failed to submit condo');
        }
      } else if (isEditMode && condoId) {
        const publishPayload: UpdateCondoData = { ...buildSubmitPayload(), isDraft: false };
        const response = await condoService.updateCondo(
          condoId,
          publishPayload,
          imageFiles.length > 0 ? imageFiles : undefined
        );
        if (response.success) {
          if (isDraft) {
            // Draft being published — navigate to list
            navigate('/admin/condos/list');
          } else {
            setSuccess('Condo updated successfully!');
            // Stay on edit page — no redirect to avoid confusion
          }
        } else {
          setError(response.message || 'Failed to update condo');
        }
      } else {
        // Create new condo
        const response = await condoService.createCondo(
          buildSubmitPayload(),
          imageFiles.length > 0 ? imageFiles : undefined
        );
        if (response.success) {
          setSuccess('Condo created successfully!');
          // Reset form
          setFormData({
            name: '',
            location: { borough: '', neighborhood: '', address: '' },
            price: '',
            bedrooms: '',
            bathrooms: '',
            sqft: '',
            details: { amenities: [], description: '' },
            isActive: true,
            listingUrl: '',
            listingSource: 'streeteasy'
          });
          setImageFiles([]);
          setPreviewUrls([]);
          setExistingImageUrls([]);
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
        } else {
          setError(response.message || 'Failed to create condo');
        }
      }
    } catch (err: any) {
      console.error('Submit error:', err);
      setError(err.message || 'An error occurred. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-paper relative">
        <BackgroundShapes />
        <div className="container mx-auto px-4 py-12 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="text-coffee text-xl">Loading condo data...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-paper relative">
      <BackgroundShapes />

      <div className="container mx-auto px-4 py-8 relative z-10">
        <div className="max-w-4xl mx-auto">
          {/* Header row: back button + title */}
          <div className="flex items-center mb-8">
            <button
              type="button"
              onClick={() => { if (isAdminMode) navigate('/admin/dashboard'); else navigate(-1); }}
              className="bg-cream border-2 border-coffee text-coffee px-3 py-2 rounded-lg hover:bg-white transition-colors shadow-sm text-sm font-medium shrink-0"
            >
              {isAdminMode ? '← Dashboard' : '← Back'}
            </button>
            <div className="flex-1 text-center">
              <h1 className="text-4xl font-bold text-coffee mb-2">
                {!isAdminMode ? 'List Your Condo' : isEditMode ? 'Edit Condo' : 'Create New Condo'}
              </h1>
              <p className="text-coffee opacity-80">
                {!isAdminMode
                  ? 'Submit your condo for admin review. Once approved, it will appear in listings.'
                  : isEditMode
                  ? 'Update condo information'
                  : 'Fill in the details to create a new condo listing'}
              </p>
            </div>
          </div>

          {/* Success Message */}
          {success && (
            <div className="bg-green-100 border-2 border-green-400 text-green-700 px-4 py-3 rounded-lg mb-6">
              {success}
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="bg-red-100 border-2 border-red-400 text-red-700 px-4 py-3 rounded-lg mb-6">
              {error}
            </div>
          )}

          {/* Form */}
          <div className="bg-cream border-2 border-coffee rounded-xl p-8 shadow-md">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Name */}
              <div>
                <label htmlFor="name" className="block text-coffee text-sm font-medium mb-2">
                  Listing Name *
                </label>
                <input
                  type="text"
                  id="name"
                  value={formData.name}
                  onChange={(e) => updateField('name', e.target.value)}
                  disabled={!isAdminMode && isEditMode}
                  className={`w-full px-4 py-3 rounded-lg border ${
                    validationErrors.name ? 'border-red-400' : 'border-coffee'
                  } text-coffee placeholder-coffee placeholder-opacity-50 focus:outline-none focus:ring-2 focus:ring-terracotta focus:border-terracotta transition-colors ${
                    !isAdminMode && isEditMode ? 'bg-paper opacity-60 cursor-not-allowed' : 'bg-white'
                  }`}
                  placeholder="Enter listing name"
                  required
                />
                {validationErrors.name && (
                  <p className="text-red-600 text-sm mt-1">{validationErrors.name}</p>
                )}
              </div>

              {/* Image Upload */}
              <div>
                <label className="block text-coffee text-sm font-medium mb-2">
                  Listing Images (up to 5)
                </label>
                <div className="space-y-4">
                  {/* Existing Images Preview */}
                  {existingImageUrls.length > 0 && previewUrls.length === 0 && (
                    <div className="space-y-3">
                      <p className="text-coffee opacity-80 text-sm">Current images:</p>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                        {existingImageUrls.map((url, index) => (
                          <div key={index} className="relative group">
                            <img
                              src={url}
                              alt={`Current listing ${index + 1}`}
                              className="w-full h-48 object-cover rounded-lg border-2 border-coffee"
                            />
                            <button
                              type="button"
                              onClick={() => removeExistingImage(index)}
                              className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                              title="Remove image"
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* New Image Previews */}
                  {previewUrls.length > 0 && (
                    <div className="space-y-3">
                      <p className="text-coffee opacity-80 text-sm">New images:</p>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                        {previewUrls.map((url, index) => (
                          <div key={index} className="relative group">
                            <img
                              src={url}
                              alt={`Preview ${index + 1}`}
                              className="w-full h-48 object-cover rounded-lg border-2 border-coffee"
                            />
                            <button
                              type="button"
                              onClick={() => removeImage(index)}
                              className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                              title="Remove image"
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* File Input */}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/webp"
                    multiple
                    onChange={handleFileInputChange}
                    className="hidden"
                    disabled={imageFiles.length + existingImageUrls.length >= MAX_CONDO_IMAGES}
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="bg-white border-2 border-coffee text-coffee px-4 py-2 rounded-lg hover:bg-cream transition-colors"
                    disabled={imageFiles.length + existingImageUrls.length >= MAX_CONDO_IMAGES}
                  >
                    {imageFiles.length + existingImageUrls.length > 0
                      ? `Add More Images (${imageFiles.length + existingImageUrls.length}/${MAX_CONDO_IMAGES})`
                      : 'Select Images'}
                  </button>
                  {(imageFiles.length + existingImageUrls.length >= MAX_CONDO_IMAGES) && (
                    <p className="text-coffee opacity-70 text-sm">Maximum {MAX_CONDO_IMAGES} images allowed</p>
                  )}
                </div>
              </div>

              {/* Location */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label htmlFor="borough" className="block text-coffee text-sm font-medium mb-2">
                    Borough *
                  </label>
                  <select
                    id="borough"
                    value={formData.location.borough}
                    onChange={(e) => {
                      updateField('location.borough', e.target.value);
                      updateField('location.neighborhood', '');
                    }}
                    className={`w-full px-4 py-3 rounded-lg bg-white border ${
                      validationErrors['location.borough'] ? 'border-red-400' : 'border-coffee'
                    } text-coffee focus:outline-none focus:ring-2 focus:ring-terracotta focus:border-terracotta transition-colors`}
                    required
                  >
                    <option value="" className="text-gray-800">Select borough</option>
                    {boroughs.map(borough => (
                      <option key={borough.value} value={borough.value} className="text-gray-800">
                        {borough.label}
                      </option>
                    ))}
                  </select>
                  {validationErrors['location.borough'] && (
                    <p className="text-red-600 text-sm mt-1">{validationErrors['location.borough']}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="neighborhood" className="block text-coffee text-sm font-medium mb-2">
                    Neighborhood *
                  </label>
                  <select
                    id="neighborhood"
                    value={formData.location.neighborhood}
                    onChange={(e) => updateField('location.neighborhood', e.target.value)}
                    disabled={!formData.location.borough}
                    required
                    className={`w-full px-4 py-3 rounded-lg bg-white border ${
                      validationErrors['location.neighborhood'] ? 'border-red-400' : 'border-coffee'
                    } text-coffee focus:outline-none focus:ring-2 focus:ring-terracotta focus:border-terracotta transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    <option value="">
                      {formData.location.borough ? 'Select neighborhood' : 'Select borough first'}
                    </option>
                    {(neighborhoods as Record<string, { value: string; label: string }[]>)[formData.location.borough]?.map(n => (
                      <option key={n.value} value={n.value}>{n.label}</option>
                    ))}
                  </select>
                  {validationErrors['location.neighborhood'] && (
                    <p className="text-red-600 text-sm mt-1">{validationErrors['location.neighborhood']}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="address" className="block text-coffee text-sm font-medium mb-2">
                    Address
                  </label>
                  <input
                    type="text"
                    id="address"
                    value={formData.location.address}
                    onChange={(e) => updateField('location.address', e.target.value)}
                    className="w-full px-4 py-3 rounded-lg bg-white border border-coffee text-coffee placeholder-coffee placeholder-opacity-50 focus:outline-none focus:ring-2 focus:ring-terracotta focus:border-terracotta transition-colors"
                    placeholder="Enter address"
                  />
                </div>
              </div>

              {/* Price, Bedrooms, Bathrooms, Sqft */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <label htmlFor="price" className="block text-coffee text-sm font-medium mb-2">
                    Price ($/mo) *
                  </label>
                  <input
                    type="number"
                    id="price"
                    min="0"
                    value={formData.price}
                    onChange={(e) => updateField('price', e.target.value === '' ? '' : parseFloat(e.target.value))}
                    required
                    className={`w-full px-4 py-3 rounded-lg bg-white border ${
                      validationErrors.price ? 'border-red-400' : 'border-coffee'
                    } text-coffee focus:outline-none focus:ring-2 focus:ring-terracotta focus:border-terracotta transition-colors`}
                    placeholder="e.g. 2500"
                  />
                  {validationErrors.price && (
                    <p className="text-red-600 text-sm mt-1">{validationErrors.price}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="bedrooms" className="block text-coffee text-sm font-medium mb-2">
                    Bedrooms *
                  </label>
                  <input
                    type="number"
                    id="bedrooms"
                    min="0"
                    value={formData.bedrooms}
                    onChange={(e) => updateField('bedrooms', e.target.value === '' ? '' : parseInt(e.target.value))}
                    required
                    className={`w-full px-4 py-3 rounded-lg bg-white border ${
                      validationErrors.bedrooms ? 'border-red-400' : 'border-coffee'
                    } text-coffee focus:outline-none focus:ring-2 focus:ring-terracotta focus:border-terracotta transition-colors`}
                    placeholder="0 = studio"
                  />
                  {validationErrors.bedrooms && (
                    <p className="text-red-600 text-sm mt-1">{validationErrors.bedrooms}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="bathrooms" className="block text-coffee text-sm font-medium mb-2">
                    Bathrooms *
                  </label>
                  <input
                    type="number"
                    id="bathrooms"
                    min="0"
                    step="0.5"
                    value={formData.bathrooms}
                    onChange={(e) => updateField('bathrooms', e.target.value === '' ? '' : parseFloat(e.target.value))}
                    required
                    className={`w-full px-4 py-3 rounded-lg bg-white border ${
                      validationErrors.bathrooms ? 'border-red-400' : 'border-coffee'
                    } text-coffee focus:outline-none focus:ring-2 focus:ring-terracotta focus:border-terracotta transition-colors`}
                    placeholder="e.g. 1"
                  />
                  {validationErrors.bathrooms && (
                    <p className="text-red-600 text-sm mt-1">{validationErrors.bathrooms}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="sqft" className="block text-coffee text-sm font-medium mb-2">
                    Sqft
                  </label>
                  <input
                    type="number"
                    id="sqft"
                    min="0"
                    value={formData.sqft}
                    onChange={(e) => updateField('sqft', e.target.value === '' ? '' : parseInt(e.target.value))}
                    className={`w-full px-4 py-3 rounded-lg bg-white border ${
                      validationErrors.sqft ? 'border-red-400' : 'border-coffee'
                    } text-coffee focus:outline-none focus:ring-2 focus:ring-terracotta focus:border-terracotta transition-colors`}
                    placeholder="e.g. 850"
                  />
                  {validationErrors.sqft && (
                    <p className="text-red-600 text-sm mt-1">{validationErrors.sqft}</p>
                  )}
                </div>
              </div>

              {/* Listing Info */}
              <div>
                <label className="block text-coffee text-sm font-medium mb-2">
                  Listing Info (optional)
                </label>
                <div className="flex gap-3">
                  <input
                    type="url"
                    value={formData.listingUrl}
                    onChange={(e) => updateField('listingUrl', e.target.value)}
                    className="flex-1 px-4 py-3 rounded-lg bg-white border border-coffee text-coffee placeholder-coffee placeholder-opacity-50 focus:outline-none focus:ring-2 focus:ring-terracotta focus:border-terracotta transition-colors"
                    placeholder="https://streeteasy.com/..."
                  />
                  <select
                    value={formData.listingSource}
                    onChange={(e) => updateField('listingSource', e.target.value)}
                    className="px-4 py-3 rounded-lg bg-white border border-coffee text-coffee focus:outline-none focus:ring-2 focus:ring-terracotta focus:border-terracotta transition-colors"
                  >
                    <option value="streeteasy">StreetEasy</option>
                    <option value="zillow">Zillow</option>
                    <option value="apartments.com">Apartments.com</option>
                  </select>
                </div>
              </div>

              {/* Amenities */}
              <div>
                <label className="block text-coffee text-sm font-medium mb-2">
                  Amenities
                </label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={amenityInput}
                    onChange={(e) => setAmenityInput(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addAmenity();
                      }
                    }}
                    placeholder="Enter amenity (e.g. gym, pool)"
                    className="flex-1 px-4 py-2 rounded-lg bg-white border border-coffee text-coffee placeholder-coffee placeholder-opacity-50 focus:outline-none focus:ring-2 focus:ring-terracotta focus:border-terracotta"
                  />
                  <button
                    type="button"
                    onClick={addAmenity}
                    className="bg-white border-2 border-coffee text-coffee px-4 py-2 rounded-lg hover:bg-cream transition-colors"
                  >
                    Add
                  </button>
                </div>
                {formData.details?.amenities && formData.details.amenities.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {formData.details.amenities.map((amenity, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center gap-1 bg-white border border-coffee text-coffee px-3 py-1 rounded-full text-sm"
                      >
                        {amenity}
                        <button
                          type="button"
                          onClick={() => removeAmenity(index)}
                          className="text-coffee hover:text-terracotta transition-colors"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Description */}
              <div>
                <label htmlFor="description" className="block text-coffee text-sm font-medium mb-2">
                  Description
                </label>
                <textarea
                  id="description"
                  value={formData.details?.description || ''}
                  onChange={(e) => updateField('details.description', e.target.value)}
                  rows={4}
                  className="w-full px-4 py-3 rounded-lg bg-white border border-coffee text-coffee placeholder-coffee placeholder-opacity-50 focus:outline-none focus:ring-2 focus:ring-terracotta focus:border-terracotta transition-colors resize-vertical"
                  placeholder="Enter condo description"
                />
              </div>


              {/* Is Active — admin only */}
              {isAdminMode && (
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="isActive"
                    checked={formData.isActive}
                    onChange={(e) => updateField('isActive', e.target.checked)}
                    className="w-4 h-4 text-terracotta border-coffee rounded focus:ring-terracotta"
                  />
                  <label htmlFor="isActive" className="ml-2 text-coffee text-sm">
                    Active (visible to users)
                  </label>
                </div>
              )}

              {/* Submit Buttons */}
              <div className="flex gap-4 pt-4">
                {!isAdminMode && isEditMode ? (
                  // User edit mode: Save as Draft + Submit for Review
                  <>
                    <button
                      type="button"
                      onClick={handleSaveDraft}
                      disabled={savingDraft || submitting}
                      className="flex-1 bg-white text-coffee border-2 border-coffee px-6 py-3 rounded-lg hover:bg-cream transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed shadow-[2px_2px_0px_#594A42]"
                    >
                      {savingDraft ? 'Saving...' : 'Save as Draft'}
                    </button>
                    <button
                      type="submit"
                      disabled={submitting || savingDraft}
                      className="flex-1 bg-terracotta text-white border-2 border-coffee px-6 py-3 rounded-lg hover:bg-terracotta/90 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed shadow-[2px_2px_0px_#594A42]"
                    >
                      {submitting ? 'Submitting...' : 'Submit for Review'}
                    </button>
                  </>
                ) : isDraft ? (
                  // Draft mode: Save (draft) + Create Condo (publish)
                  <>
                    <button
                      type="button"
                      onClick={handleSaveDraft}
                      disabled={savingDraft || submitting}
                      className="flex-1 bg-white text-coffee border-2 border-coffee px-6 py-3 rounded-lg hover:bg-cream transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {savingDraft ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      type="submit"
                      disabled={submitting || savingDraft}
                      className="flex-1 bg-terracotta text-white border-2 border-coffee px-6 py-3 rounded-lg hover:bg-terracotta/90 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {submitting ? 'Publishing...' : 'Create Listing'}
                    </button>
                  </>
                ) : isEditMode ? (
                  // Published condo edit mode: Update + Cancel
                  <>
                    <button
                      type="submit"
                      disabled={submitting}
                      className="flex-1 bg-terracotta text-white border-2 border-coffee px-6 py-3 rounded-lg hover:bg-terracotta/90 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {submitting ? 'Updating...' : 'Update Listing'}
                    </button>
                    <button
                      type="button"
                      onClick={() => navigate('/admin/condos/list')}
                      className="bg-white border-2 border-coffee text-coffee px-6 py-3 rounded-lg hover:bg-cream transition-colors"
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  // New condo create mode: Save (draft) + Create Listing / Submit for Review
                  <>
                    <button
                      type="button"
                      onClick={handleSaveDraft}
                      disabled={savingDraft || submitting}
                      className="flex-1 bg-white text-coffee border-2 border-coffee px-6 py-3 rounded-lg hover:bg-cream transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {savingDraft ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      type="submit"
                      disabled={submitting || savingDraft}
                      className="flex-1 bg-terracotta text-white border-2 border-coffee px-6 py-3 rounded-lg hover:bg-terracotta/90 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {submitting
                        ? (isAdminMode ? 'Creating...' : 'Submitting...')
                        : (isAdminMode ? 'Create Listing' : 'Submit for Review')}
                    </button>
                  </>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminCondoFillPage;
